import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../index";

interface Zone {
  ZONE_ID: number;
  ZONE: string;
}

interface Question {
  ID: number;
  Question: string;
}

interface QuestionsBySection {
  Safety: Question[];
  People: Question[];
  Accuracy: Question[];
  Costs: Question[];
  Reliability: Question[];
}

interface LeadershipWalk {
  id: number;
  walkId?: number; // Add walkId to interface
  date?: string;
  zone?: string;
  Zone?: string; // API returns uppercase
  frequency?: string;
  Frequency?: string; // API returns uppercase
  status: string;
  createdBy: string;
  observation: string;
  miscellaneousObservations: string;
  created?: string;
  Created?: string; // API returns uppercase
  modified: string;
  modifiedBy: string;
}

interface CentralizedError {
  id: string;
  apiName: string;
  message: string;
  timestamp: number;
  context?: any;
}

interface LeadershipWalkState {
  zones: Zone[];
  loading: boolean;
  error: string | null;
  selectedZone: Zone[];
  selectedFrequency: string | null;
  walkId: number | null;
  questions: {
    Safety: any[];
    People: any[];
    Accuracy: any[];
    Costs: any[];
    Reliability: any[];
  };
  leadershipWalks: LeadershipWalk[];
  selectedLeadershipWalk: LeadershipWalk | null;
  successMessage: string | null;
  // Centralized error handling
  centralizedErrors: CentralizedError[];
  isErrorModalVisible: boolean;
}

const initialState: LeadershipWalkState = {
  zones: [],
  loading: false,
  error: null,
  selectedZone: [],
  selectedFrequency: null,
  walkId: null,
  questions: {
    Safety: [],
    People: [],
    Accuracy: [],
    Costs: [],
    Reliability: [],
  },
  successMessage: null,
  leadershipWalks: [],
  selectedLeadershipWalk: null,
  centralizedErrors: [],
  isErrorModalVisible: false,
};

// Selectors
const selectZones = (state: RootState) => state.leadershipWalk.zones;
const selectLoading = (state: RootState) => state.leadershipWalk.loading;
const selectError = (state: RootState) => state.leadershipWalk.error;
const selectSelectedZone = (state: RootState) =>
  state.leadershipWalk.selectedZone;
const selectSelectedFrequency = (state: RootState) =>
  state.leadershipWalk.selectedFrequency;
const selectQuestions = (state: RootState) => state.leadershipWalk.questions;
const selectWalkId = (state: RootState) => state.leadershipWalk.walkId;
const selectLeadershipWalks = (state: RootState) =>
  state.leadershipWalk.leadershipWalks;
const selectSelectedLeadershipWalk = (state: RootState) =>
  state.leadershipWalk.selectedLeadershipWalk;
const selectSuccessMessage = (state: RootState) =>
  state.leadershipWalk.successMessage;
const selectCentralizedErrors = (state: RootState) =>
  state.leadershipWalk.centralizedErrors;
const selectIsErrorModalVisible = (state: RootState) =>
  state.leadershipWalk.isErrorModalVisible;

const leadershipWalkSlice = createSlice({
  name: "leadershipWalk",
  initialState,
  reducers: {
    setZones: (state, action: PayloadAction<Zone[]>) => {
      state.zones = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSelectedZone: (state, action: PayloadAction<Zone[]>) => {
      state.selectedZone = action.payload;
    },
    setSelectedFrequency: (state, action: PayloadAction<string | null>) => {
      state.selectedFrequency = action.payload;
    },
    setQuestions: (state, action: PayloadAction<QuestionsBySection>) => {
      state.questions = action.payload;
    },
    setWalkId: (state, action: PayloadAction<number | null>) => {
      console.log("setWalkId reducer called with:", action.payload);
      state.walkId = action.payload;
    },
    setLeadershipWalks: (state, action: PayloadAction<LeadershipWalk[]>) => {
      state.leadershipWalks = action.payload;
      // If there are walks, try to update walkId from them
      if (action.payload.length > 0) {
        const walkId = action.payload[0].walkId || action.payload[0].id;
        if (walkId && !state.walkId) {
          state.walkId = walkId;
        }
      }
    },
    resetFilters: (state) => {
      state.selectedZone = [];
      state.selectedFrequency = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetWalkId: (state) => {
      state.walkId = null;
    },
    setSelectedLeadershipWalk: (
      state,
      action: PayloadAction<LeadershipWalk | null>
    ) => {
      state.selectedLeadershipWalk = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    // Centralized error handling reducers
    addCentralizedError: (state, action: PayloadAction<CentralizedError>) => {
      state.centralizedErrors.push(action.payload);
    },
    clearCentralizedErrors: (state) => {
      state.centralizedErrors = [];
    },
    setErrorModalVisible: (state, action: PayloadAction<boolean>) => {
      state.isErrorModalVisible = action.payload;
    },
  },
});

export const {
  setZones,
  setLoading,
  setError,
  setSelectedZone,
  setSelectedFrequency,
  resetFilters,
  clearError,
  setQuestions,
  setWalkId,
  resetWalkId,
  setLeadershipWalks,
  setSelectedLeadershipWalk,
  setSuccessMessage,
  addCentralizedError,
  clearCentralizedErrors,
  setErrorModalVisible,
} = leadershipWalkSlice.actions;

// Export selectors
export {
  selectZones,
  selectLoading,
  selectError,
  selectSelectedZone,
  selectSelectedFrequency,
  selectQuestions,
  selectWalkId,
  selectLeadershipWalks,
  selectSelectedLeadershipWalk,
  selectSuccessMessage,
  selectCentralizedErrors,
  selectIsErrorModalVisible,
};

export default leadershipWalkSlice.reducer;
